import Button, {StyleType} from "patient-ping-remedy/packages/button";
import Icon, {ICONS} from "patient-ping-remedy/packages/icon";
import Spacer from "patient-ping-remedy/packages/spacer";
import {sizes} from "patient-ping-remedy/packages/theme";
import {useState} from "react";
import {css} from "@emotion/css";
import PermissionsHelpers from "@/helpers/permissions_helpers";
import {FeatureNew, FeaturePermissionLevel} from "@/api/dto/permissions";
import {useAuthStore} from "@/store/auth_store";
import {useActiveRoster} from "@/store/roster_store";
import ScheduleAppointmentModal from "@/components/pages/tasks/task_edit/progress/bh_journey/ScheduleAppointmentModal";
import {CareJourneyDetailsResponse, CareJourneyStep} from "@/api/dto/care-journey";
import {ClientProfile} from "@/api/dto/client-profile";
import SchedulingSealModal from "@/components/pages/tasks/task_edit/progress/bh_journey/SchedulingSealModal";
import {
  TERMINAL_APPOINTMENT_STATUSES
} from "@/components/pages/tasks/task_edit/progress/bh_journey/ScheduleAppointmentsStep.constants";
import {FilterInvalidate} from "@common/FilterComponent";

type Props = {
    fetching: boolean;
    careJourneyDetails: CareJourneyDetailsResponse;
    careJourneyStep: CareJourneyStep;
    patient: ClientProfile;
    invalidate: FilterInvalidate;
}

const ScheduleAppointmentActions = (props : Props) => {
  const {currentUser} = useAuthStore();
  const currentRoster = useActiveRoster();

  const [scheduleAppointmentModalOpen, setScheduleAppointmentModalOpen] = useState(false);
  const [schedulingSealModalOpen, setSchedulingSealModalOpen] = useState(false);

  const canActOnTasks = !!currentUser && !!currentRoster &&
    PermissionsHelpers.canForUserFeaturePermissionRosterNew(
      currentUser,
      currentRoster,
      FeaturePermissionLevel.ACT,
      FeatureNew.TASKS
    );

  const sealed = !!props.careJourneyDetails.orchestrationEngineDetails?.appointmentSealed;

  // Scheduling can only be sealed once every appointment sub-journey has reached a terminal state.
  // An empty list is vacuously terminal, so sealing with no appointments is allowed.
  const appointmentsTerminal = (props.careJourneyStep.appointments ?? [])
    .every(appointment => TERMINAL_APPOINTMENT_STATUSES.has(appointment.status?.toLowerCase()));

  const disableSealButton = !canActOnTasks || props.fetching || (!sealed && !appointmentsTerminal);

  return <div className={css({
    display: 'flex',
    alignItems: 'center',
    gap: sizes.xSmall,
  })}>
    <Button
      onClick={() => setSchedulingSealModalOpen(true)}
      disabled={disableSealButton}
      styleType={StyleType.TERTIARY}
    >
      {!props.careJourneyDetails.orchestrationEngineDetails?.appointmentSealed &&
        <>
          <Icon iconClass={ICONS.check} />
          <Spacer itemWidth={sizes.xSmall}/>
        </>
      }
      {props.careJourneyDetails.orchestrationEngineDetails?.appointmentSealed ? 'Reopen scheduling' : 'Done Scheduling' }
    </Button>

    <Button
      onClick={() => setScheduleAppointmentModalOpen(true)}
      disabled={!canActOnTasks || props.fetching || props.careJourneyDetails.orchestrationEngineDetails?.appointmentSealed}
      styleType={StyleType.SECONDARY}
    >
      <Icon iconClass={ICONS.plus} />
      <Spacer itemWidth={sizes.xSmall}/>
      Add Appointment
    </Button>

    <ScheduleAppointmentModal
      open={scheduleAppointmentModalOpen}
      close={() => setScheduleAppointmentModalOpen(false)}
      careJourneyDetails={props.careJourneyDetails}
      patient={props.patient}
      careJourneyStep={props.careJourneyStep}
    />

    <SchedulingSealModal
      open={schedulingSealModalOpen}
      close={() => setSchedulingSealModalOpen(false)}
      careJourneyDetails={props.careJourneyDetails}
      invalidate={props.invalidate}
    />
  </div>;
};

export default ScheduleAppointmentActions;
